<template>
  <div class="nfse-row-actions" v-if="hasAnyAction">
    <button
      type="button"
      class="btn btn-light shadow-sm"
      @click.stop="toggleOpen"
    >
      <i class="fas fa-ellipsis-v"></i>
    </button>
    <div class="nfse-row-actions-menu" v-if="open" @click.stop>
      <button
        type="button"
        class="dropdown-item-custom"
        v-if="canPrint"
        :disabled="loadingAction === 'print'"
        @click="handlePrint"
      >
        <i class="fas fa-print me-2"></i>Imprimir
      </button>
      <button
        type="button"
        class="dropdown-item-custom"
        v-if="canDownloadPdf"
        :disabled="loadingAction === 'download-pdf'"
        @click="handleDownload('pdf')"
      >
        <i class="fas fa-file-pdf me-2"></i>Baixar PDF
      </button>
      <button
        type="button"
        class="dropdown-item-custom"
        v-if="canDownloadXml"
        :disabled="loadingAction === 'download-xml'"
        @click="handleDownload('xml')"
      >
        <i class="fas fa-file-code me-2"></i>Baixar XML
      </button>
      <button
        type="button"
        class="dropdown-item-custom"
        v-if="canViewError"
        @click="handleViewError"
      >
        <i class="fas fa-triangle-exclamation me-2"></i>Ver erro
      </button>
      <button
        type="button"
        class="dropdown-item-custom"
        v-if="canCorrect"
        @click="handleCorrect"
      >
        <i class="fas fa-pen me-2"></i>Corrigir e reemitir
      </button>
      <button
        type="button"
        class="dropdown-item-custom"
        v-if="canDuplicate"
        @click="handleDuplicate"
      >
        <i class="fas fa-copy me-2"></i>Duplicar
      </button>
    </div>
  </div>
</template>
<script>
import api from "@/services/api";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "nfse-row-actions",
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  emits: ["view-error"],
  data() {
    return {
      open: false,
      loadingAction: null,
    };
  },
  computed: {
    canPrint() {
      return ["authorized", "cancelled"].includes(this.item.status);
    },
    canDownloadPdf() {
      return ["authorized", "cancelled"].includes(this.item.status);
    },
    canDownloadXml() {
      return ["authorized", "cancelled"].includes(this.item.status);
    },
    canViewError() {
      return (
        ["rejected", "error", "submission_unknown"].includes(
          this.item.status
        ) && !!this.item.last_error
      );
    },
    canCorrect() {
      return ["rejected", "error"].includes(this.item.status);
    },
    canDuplicate() {
      return !["processing", "submission_unknown"].includes(this.item.status);
    },
    hasAnyAction() {
      return (
        this.canPrint ||
        this.canDownloadPdf ||
        this.canDownloadXml ||
        this.canViewError ||
        this.canCorrect ||
        this.canDuplicate
      );
    },
  },
  mounted() {
    document.addEventListener("click", this.closeOnOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeOnOutsideClick);
  },
  methods: {
    toggleOpen() {
      this.open = !this.open;
    },
    closeOnOutsideClick() {
      this.open = false;
    },
    async fetchFreshDocument() {
      const { data } = await api.get(`/company/nfse/${this.item.id}`);
      return data;
    },
    async handlePrint() {
      this.open = false;
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("O navegador bloqueou a abertura do documento. Permita pop-ups e tente novamente.");
        return;
      }
      printWindow.opener = null;
      this.loadingAction = "print";
      try {
        const fresh = await this.fetchFreshDocument();
        if (!fresh.pdf_url) {
          printWindow.close();
          toast.error("O documento PDF ainda não está disponível para esta nota.");
          return;
        }
        printWindow.addEventListener("load", () => {
          try {
            printWindow.print();
          } catch {
            // navegador pode bloquear print() em janela cross-origin; usuário imprime pela própria visualização do PDF
          }
        });
        printWindow.location.replace(fresh.pdf_url);
      } catch {
        printWindow.close();
        toast.error("Não foi possível abrir o documento para impressão.");
      } finally {
        this.loadingAction = null;
      }
    },
    async handleDownload(type) {
      this.open = false;
      this.loadingAction = `download-${type}`;
      try {
        const fresh = await this.fetchFreshDocument();
        const url = type === "pdf" ? fresh.pdf_url : fresh.xml_url;
        if (!url) {
          toast.error(
            type === "pdf"
              ? "O documento PDF ainda não está disponível para esta nota."
              : "O documento XML ainda não está disponível para esta nota."
          );
          return;
        }
        // O atributo `download` é ignorado pelo navegador em links cross-origin
        // (URL assinada do S3) sem Content-Disposition: attachment — por isso
        // tentamos buscar o arquivo como blob e baixar a partir de uma URL
        // local. Isso só funciona se o bucket permitir CORS para este
        // domínio; sem isso, o fetch falha e caímos para abrir o documento
        // (mesmo comportamento de "Ver documento") em vez de travar o usuário
        // sem o arquivo. O fix definitivo é o backend assinar a URL com
        // response-content-disposition=attachment.
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error("download failed");
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = `nfse-${this.item.numero_nfse || this.item.n_dps || this.item.id}.${type}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        } catch {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } catch {
        toast.error("Não foi possível baixar o documento. Tente novamente.");
      } finally {
        this.loadingAction = null;
      }
    },
    handleViewError() {
      this.open = false;
      this.$emit("view-error", this.item);
    },
    handleCorrect() {
      this.open = false;
      this.$router.push(`/cliente/nfse/novo/${this.item.id}`);
    },
    handleDuplicate() {
      this.open = false;
      this.$router.push(`/cliente/nfse/novo/${this.item.id}`);
    },
  },
};
</script>
<style scoped>
.nfse-row-actions {
  position: relative;
  display: inline-block;
}
.nfse-row-actions-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  min-width: 190px;
  z-index: 20;
  padding: 4px 0;
}
.dropdown-item-custom {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 14px;
  background: none;
  border: none;
  font-size: 0.9rem;
  color: #495057;
}
.dropdown-item-custom:hover:not(:disabled) {
  background-color: #f4f6f9;
}
.dropdown-item-custom:disabled {
  opacity: 0.6;
  cursor: wait;
}
</style>
